import { useCallback, useEffect, useRef, useState } from "react";
import { applicantApi, type SessionStatus, type ApiError } from "@/lib/api";

interface UseSessionResult {
  session: SessionStatus | null;
  loading: boolean;
  error: ApiError | null;
  /** Resolves true on success, false if the fetch errored (error is also set on state). */
  refresh: () => Promise<boolean>;
}

/** Fallback when the server doesn't tell us how long to wait. */
const DEFAULT_RETRY_SECONDS = 5;
/** Never wait longer than this, whatever the server says. */
const MAX_RETRY_SECONDS = 60;

/**
 * Loads and exposes the live session for `/verify/:token`.
 * Returns nulls when no token is provided (demo mode).
 *
 * ## 429s are transient, not terminal
 *
 * `ApplicantFlow` renders a full-page "We couldn't load your application" card
 * whenever `error` is set. Setting it on a 429 was wrong twice over:
 *
 *  - A rate limit is the server saying "come back shortly", not "your
 *    verification is broken."
 *  - Polling runs every 12s, so a throttled BACKGROUND tick could replace an
 *    already-working page with a full-page failure.
 *
 * And the usual way to trip the limit is retrying a provider that's failing —
 * so the applicant most likely to see it is the one already having trouble.
 * (UAT 2026-07-31: a misconfigured Microsoft secret produced exactly this.)
 *
 * So a 429 sets no error and schedules a retry using the server's own
 * `retry_after_seconds`, clamped so a bad value can't strand anyone.
 */
export function useSession(token: string | undefined): UseSessionResult {
  const [session, setSession] = useState<SessionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(token));
  const [error, setError] = useState<ApiError | null>(null);

  // Read inside the retry callback, which would otherwise close over a stale
  // value from the render that scheduled it.
  const sessionRef = useRef<SessionStatus | null>(null);
  sessionRef.current = session;

  const retryTimerRef = useRef<number | null>(null);
  const refreshRef = useRef<() => Promise<boolean>>();

  const clearRetry = () => {
    if (retryTimerRef.current !== null) {
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const refresh = useCallback(async (): Promise<boolean> => {
    if (!token) return true;

    // An explicit refresh supersedes any pending backoff — otherwise a queued
    // retry fires on top of it and burns another request against the limit.
    clearRetry();

    setLoading(true);
    setError(null);
    try {
      const next = await applicantApi.getSessionStatus(token);
      setSession(next);
      setLoading(false);
      return true;
    } catch (e) {
      const err = e as ApiError;

      if (err.status === 429) {
        const payload = err.payload as { retry_after_seconds?: number } | undefined;
        const seconds = Math.min(
          Math.max(payload?.retry_after_seconds ?? DEFAULT_RETRY_SECONDS, 1),
          MAX_RETRY_SECONDS,
        );
        retryTimerRef.current = window.setTimeout(() => {
          retryTimerRef.current = null;
          void refreshRef.current?.();
        }, seconds * 1000);

        // Stay in the loading state only while there's nothing to show. If a
        // session is already on screen the retry is entirely invisible, which
        // is the point.
        setLoading(sessionRef.current === null);
        return false;
      }

      setError(err);
      setLoading(false);
      return false;
    }
  }, [token]);

  refreshRef.current = refresh;

  useEffect(() => {
    if (!token) {
      setSession(null);
      setLoading(false);
      return;
    }
    void refresh();
  }, [token, refresh]);

  // A pending retry must not outlive the component.
  useEffect(() => clearRetry, []);

  return { session, loading, error, refresh };
}
